import {gql} from '@apollo/client';
import {entry, objectFromEntries} from '@benchling/js-utils';
import classNames from 'classnames';
import React, {useEffect} from 'react';

import {StarredItemsByObjectId, useStarredItemsQuery} from 'app-core/platform/starrable/CoreObjectStarIcon';
import isArchived from 'archive/isArchived';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {getFeatureStatus} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {ChemSearchFolderItemListingExtras} from 'chemistry/ChemistryConstants';
import DataConnector from 'connect/DataConnector';
import {FolderSection} from 'constants/FolderConstants';
import * as NavConstants from 'constants/NavConstants';
import {REGISTRIES_FINDER_ID} from 'constants/NavConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import {StorableSummaryExtras} from 'constants/SampleConstants';
import * as FileUtil from 'folder-items/FileUtil';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import InventoryFileStore from 'inventory/InventoryFileStore';
import {
  StorableValidationInfoById,
  useStorableValidationInfoQuery,
} from 'inventory/location-validation/ParentStorableValidationUtils';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import {ReviewChangeTimestamps} from 'notebook/reviews/ReviewConstants';
import {RequestSampleStatusCounts} from 'requests/fulfillments/FulfillmentConstants';
import {AllRequestSchemasQuery_requestSchemas_RequestSchemaNode as RequestSchemaGQL} from 'requests/schemas/__generated/AllRequestSchemasQuery';
import {useAllRequestSchemasQuery} from 'requests/schemas/useAllRequestSchemasQuery';
import {RequestTaskSchema} from 'requests/tasks/RequestTaskConstants';
import AssayStore from 'results/AssayStore';
import {AllRunSchemasQuery_runSchemas_RunSchemaNode as RunSchemaGQL} from 'runs/run-schemas/__generated/AllRunSchemasQuery';
import {useAllRunSchemasQuery} from 'runs/run-schemas/useAllRunSchemasQuery';
import {
  ExperimentalRolesByContainerId,
  useContainerExperimentalRoleQuery,
} from 'samples/containers/ExperimentalRoleUtils';
import {
  ContainerValidContentsInfoById,
  useContainerValidContentsQuery,
} from 'samples/containers/ValidContentsUtils';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import StorableStore from 'samples/StorableStore';
import {ValidationExtras} from 'schemas/SchemaValidationConstants';
import {
  TagSchemaSummaryWithFieldsGraphQL,
  useTagSchemaSummariesWithFields,
} from 'schemas/useTagSchemaSummariesWithFields';
import {getFiltersByKey} from 'search/FilterUtil';
import {
  AsyncFetcherLocation,
  AsyncFetcherIsFetchedState,
  Filter,
  FilterKey,
  isStorableSummary,
  ListableGroup,
  ListableItem,
  SearchParams,
  SearchResults,
  Sort,
  StringFieldFilter,
} from 'search/SearchConstants';
import EntityStore from 'stores/EntityStore';
import {StudyableItemListingExtras, useItemsInStudiesQuery} from 'studies/studyable/StudiesListingColumn';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ExpandedScrollableTable from 'ui-library/scrollable/ExpandedScrollableTable';
import {
  RowSelectionMode,
  ScrollableTableRow,
  TABLE_ROW_BODY_HEIGHT,
} from 'ui-library/scrollable/ScrollableTableConstants';
import {cycleColumnHeaderSort, makeFirstColumnFixed} from 'ui-library/scrollable/ScrollableTableUtil';
import APIUtil from 'util/APIUtil';
import preserveShallowEquality from 'util/preserveShallowEquality';
import * as ShortcutUtil from 'util/ShortcutUtil';
import {BasicEvent} from 'util/Types';

import {ExpandedItemTableQuery, ExpandedItemTableQueryVariables} from './__generated/ExpandedItemTableQuery';
import {ContainableInventoryInfo} from './ContainableInventorySummary';
import renderExpandedListing, {openExpandedContextMenu} from './RenderExpandedListing';

function getSelectedContainableTagSchemaIds(searchParams: SearchParams | undefined): Set<string> {
  if (searchParams) {
    const contentTypeFilters = getFiltersByKey(searchParams.filters, FilterKey.CONTAINER_CONTENT_SCHEMA);
    return new Set(
      contentTypeFilters.flatMap((filter) => (isStringValueFilter(filter) ? [filter.value as string] : []))
    );
  } else {
    return new Set();
  }
}

type ExpandedItemTableMixtureGraphQL = ExtractByTypename<
  ExpandedItemTableQuery['bioentities'][number],
  'MixtureNode'
>;

interface Props extends StudyableItemListingExtras {
  actionSource: ActionOrigin;
  collection?: string | null;
  emptyMessage: string;
  finderId: string;
  inventoryInfoByContainableId?: {[bioentityId: string]: ContainableInventoryInfo} | 'LOADING';
  isCompact: boolean;
  isSearch?: boolean;
  items: Array<ListableItem>;
  groups?: Array<ListableGroup>;
  mixturesById?: {[mixtureId: string]: ExpandedItemTableMixtureGraphQL};
  asyncFetcherIsFetchedState: AsyncFetcherIsFetchedState;
  onUpdateAsyncFetcherIsFetchedState: (flagName: AsyncFetcherLocation, value: boolean) => void;
  onSelectIds: (itemIds: Array<string>) => void;
  onSetColumnHeaderSort: (sort: Sort) => void;
  onToggleSelectedIds: (resultIds: Array<string>, isSelected: boolean) => void;
  onOpenItem: (item: ListableItem) => void;
  section: FolderSection | null;
  selectedIds: Array<string>;
  sorts: Array<Sort>;
  tableKey: string;
  chemSearchFolderItemListingExtras?: ChemSearchFolderItemListingExtras | null;
  defaultHiddenColumnKeys?: Array<string> | null;
  searchParams?: SearchParams;
  searchSection: NavConstants.Section;
  searchResults: SearchResults;
  rowSelectionMode?: RowSelectionMode | null;
  alwaysShowSelector?: boolean;

  /** Called when the user expands or collapses a group. */
  onToggleExpandedItem: (itemId: string, isExpanded: boolean) => void;
  /** Called when the user requests to load more child items within a group. */
  onRequestMoreChildItems: (parentItemId: string) => void;
  /** The number of additional children that will be fetched when onRequestMoreChildItems is called. */
  childPageSize: number;
  starredItemsByObjectId?: StarredItemsByObjectId;
  containerValidContentsInfoById?: ContainerValidContentsInfoById;
  experimentalRolesByContainerId?: ExperimentalRolesByContainerId;
  storableValidationInfoById?: StorableValidationInfoById;
  selectedContainableTagSchemas?: Array<TagSchemaSummaryWithFieldsGraphQL>;
  requestSchemas?: Array<RequestSchemaGQL>;
  runSchemas?: Array<RunSchemaGQL>;
}

interface EnhancedProps {
  bioentityAliasInfosByBioentityId: {[bioentityId: string]: Array<BioentityAliasInfo> | undefined};
  reviewChangeTimestampsByEntryId: {[entryId: string]: ReviewChangeTimestamps | undefined};
  validationExtrasByBioentityId: {[bioentityId: string]: ValidationExtras | undefined};
  storableSummaryExtras?: StorableSummaryExtras | null;
  sampleStatusCountsByRequestId?: {[requestId: string]: RequestSampleStatusCounts};
  requestedTaskSchemaIdsByRequestId?: {[requestId: string]: Array<string>};
  requestTaskSchemasById?: {[id: string]: RequestTaskSchema} | null;
  itemAttributes?: Array<string>;
}

interface ExpandedItemTableRow extends ScrollableTableRow {
  extra: {item?: ListableItem; onClick?: () => void; groupId?: string};
}

export class ExpandedItemTable extends React.PureComponent<Props & EnhancedProps> {
  override componentDidUpdate(prevProps: Props & EnhancedProps): void {
    const current =
      this.props.asyncFetcherIsFetchedState[AsyncFetcherLocation.EXPANDED_ITEM_TABLE_ENHANCE_PROPS];
    if (!current) {
      this.props.onUpdateAsyncFetcherIsFetchedState(
        AsyncFetcherLocation.EXPANDED_ITEM_TABLE_ENHANCE_PROPS,
        true
      );
    }
  }

  _handleClickRow = (row: ExpandedItemTableRow): void => {
    if (row.extra.onClick) {
      row.extra.onClick();
    } else if (row.extra.item) {
      this.props.onOpenItem(row.extra.item);
    }
  };

  _handleSetSort = (sortKey: string): void => {
    const newSort = cycleColumnHeaderSort(this.props.sorts, sortKey);
    this.props.onSetColumnHeaderSort(newSort);
  };

  _handleContextMenu = (row: ExpandedItemTableRow, e: BasicEvent): void => {
    const isSignedIn = EntityStore.currentUser() != null;
    if (!isSignedIn) {
      return;
    }
    const {item} = row.extra;
    if (item == null) {
      return;
    }
    this.props.onSelectIds([item.id]);
    openExpandedContextMenu(item, e, {
      finderId: this.props.finderId,
      isListing: !this.props.isSearch,
      actionSource: this.props.actionSource,
    });
  };

  /**
   * There are two different meanings of the word "expanded" here.
   *   - The expanded sidepanel that takes up the full width of the screen
   *     (as in ExpandedItemTable, ExpandedItemTableRow)
   *   - An expanded row that reveals additional rows below it
   *     (as in onToggleExpandedItem, _handleToggleExpandedRow)
   *
   * TODO(Search): Rename one or the other.
   */
  _handleToggleExpandedRow = (row: ExpandedItemTableRow, isExpanded: boolean): void => {
    this.props.onToggleExpandedItem(row.id, isExpanded);
  };

  _handleToggleSelectResults = (rows: Array<ExpandedItemTableRow>, isSelected: boolean): void => {
    const selectedIds = rows.map((row) => row.extra.item?.id ?? row.id);
    this.props.onToggleSelectedIds(selectedIds, isSelected);
  };

  override render(): JSX.Element {
    if (!this.props.items.length && !this.props.groups?.length) {
      return (
        <div
          className="sidepanel-fadedMessage expandedBrowser-emptyMessage"
          {...getTestProps({component: 'ExpandedItemsTable', element: 'emptyMessage'})}
        >
          {this.props.emptyMessage}
        </div>
      );
    }

    const requestSchemas = this.props.requestSchemas
      ? this.props.requestSchemas.map((schema) => graphQLToREST('RequestSchema', schema))
      : this.props.requestSchemas;
    const runSchemas = this.props.runSchemas
      ? this.props.runSchemas.map((schema) => graphQLToREST('RunSchema', schema))
      : this.props.runSchemas;

    const {
      columns,
      rowDataByItemId,
      groupRowDataByItemId,
      iconsByItemId,
      rowContentHeightByItemId,
      groupRowContentHeightByItemId,
      clickHandlersByItemId,
    } = renderExpandedListing(
      this.props.items,
      this.props.sorts,
      {
        bioentityAliasInfosByBioentityId: this.props.bioentityAliasInfosByBioentityId,
        reviewChangeTimestampsByEntryId: this.props.reviewChangeTimestampsByEntryId,
        validationExtrasByBioentityId: this.props.validationExtrasByBioentityId,
        isRegistry: this.props.finderId === REGISTRIES_FINDER_ID,
        section: this.props.section,
        collection: this.props.collection,
        shouldShowShortcuts: ShortcutUtil.shouldDetectShortcuts(this.props.finderId),
        storableSummaryExtras: this.props.storableSummaryExtras,
        selectedContainableTagSchemas: this.props.selectedContainableTagSchemas,
        inventoryInfoByContainableId: this.props.inventoryInfoByContainableId,
        mixturesById: this.props.mixturesById,
        chemSearchFolderItemListingExtras: this.props.chemSearchFolderItemListingExtras,
        sampleStatusCountsByRequestId: this.props.sampleStatusCountsByRequestId,
        requestedTaskSchemaIdsByRequestId: this.props.requestedTaskSchemaIdsByRequestId,
        requestTaskSchemasById: this.props.requestTaskSchemasById,
        requestSchemas,
        runSchemas,
        searchSection: this.props.searchSection,
        itemAttributes: this.props.itemAttributes,
        starredItemsByObjectId: this.props.starredItemsByObjectId,
        itemStudyAttributes: this.props.itemStudyAttributes,
        containerValidContentsInfoById: this.props.containerValidContentsInfoById,
        experimentalRolesByContainerId: this.props.experimentalRolesByContainerId,
        storableValidationInfoById: this.props.storableValidationInfoById,
      },
      this.props.groups
    );

    const rows: Array<ExpandedItemTableRow> = [];
    for (const item of this.props.items) {
      const featureStatus = getFeatureStatus({
        feature: StandardFeature.OPEN,
        item,
        itemState: {isRegistered: FileUtil.isInRegistry(item)},
      });

      rows.push({
        id: item.id,
        isFaded: isArchived(item) || !featureStatus.enabled,
        data: rowDataByItemId[item.id],
        icon: iconsByItemId[item.id],
        extra: {
          item,
          ...(clickHandlersByItemId[item.id] != null
            ? {
                onClick: () => {
                  const clickHandler = clickHandlersByItemId[item.id];
                  clickHandler(item, this.props.onOpenItem);
                },
              }
            : {}),
        },
        isExpandable: false,
        isExpanded: false,
        isSelected: this.props.selectedIds.includes(item.id),
        tooltipText: featureStatus.enabled ? undefined : featureStatus.userMessage,
      });
    }
    for (const group of this.props.groups ?? []) {
      rows.push({
        id: group.id,
        data: rowDataByItemId[group.id],
        // TODO: Icon for "multiple items"
        icon: iconsByItemId[group.id],
        extra: {},
        isExpandable: group.count > 0,
        isExpanded: group.isExpanded,
        isSelectorPresent: false,
        isSelected: false,
      });
      if (group.isExpanded) {
        for (const item of group.items) {
          const featureStatus = getFeatureStatus({
            feature: StandardFeature.OPEN,
            item,
            itemState: {isRegistered: FileUtil.isInRegistry(item)},
          });

          rows.push({
            id: item.id,
            isFaded: Boolean(item.archiveRecord) || !featureStatus.enabled,
            data: groupRowDataByItemId[group.id][item.id],
            icon: iconsByItemId[item.id],
            extra: {
              item,
              groupId: group.id,
              ...(clickHandlersByItemId[item.id] != null
                ? {
                    onClick: () => {
                      const clickHandler = clickHandlersByItemId[item.id];
                      clickHandler(item, this.props.onOpenItem);
                    },
                  }
                : {}),
            },
            isSelected: this.props.selectedIds.includes(item.id),
            tooltipText: featureStatus.enabled ? undefined : featureStatus.userMessage,
          });
        }

        if (group.areChildrenLoading) {
          rows.push({
            id: `${group.id}_loading`,
            data: {
              [columns[0].accessorKey]: <CenteredHorizontalThrobber suppressPadding={true} />,
            },
            extra: {},
            isSelectorPresent: false,
            // Does not apply styles, just used for selectors in cypress tests.
            rowOnlyClassName: 'ExpandedItemTable-loading-row',
          });
        }

        const numChildrenLoaded = group.items.length;
        const numChildrenLoadable = Math.min(group.count - numChildrenLoaded, this.props.childPageSize);
        if (numChildrenLoaded > 0 && numChildrenLoadable > 0) {
          rows.push({
            id: `LOAD_MORE::${group.id}`,
            data: {
              name: <a href="#">Load more ({numChildrenLoadable})</a>,
            },
            extra: {
              onClick: () => {
                this.props.onRequestMoreChildItems(group.id);
              },
            },
            isSelectorPresent: false,
          });
        }
      }

      // TODO(BNCH-45990): Render placeholder rows for first page of children, if not yet loaded
    }

    return (
      <div className={classNames('u-flex-display', 'u-flex-grow')}>
        <ExpandedScrollableTable
          canFilterColumns={true}
          canResizeColumns={true}
          rowSelectionMode={this.props.rowSelectionMode ?? RowSelectionMode.MULTIPLE}
          alwaysShowSelector={this.props.alwaysShowSelector}
          className="u-flex-grow"
          columns={makeFirstColumnFixed(columns)}
          getRowContentHeight={(rowIndex: number) => {
            const row = rows[rowIndex];
            const heightMap = row.extra?.groupId
              ? groupRowContentHeightByItemId[row.extra.groupId]
              : rowContentHeightByItemId;
            return heightMap[row.id] ?? TABLE_ROW_BODY_HEIGHT;
          }}
          isCompact={this.props.isCompact}
          matchParentHeight={true}
          onChangeSort={this._handleSetSort}
          onRowClick={this._handleClickRow}
          onRowContextMenu={this._handleContextMenu}
          onRowToggleExpanded={this._handleToggleExpandedRow}
          onRowsSelect={this._handleToggleSelectResults}
          rows={rows}
          tableKey={this.props.tableKey}
          defaultHiddenColumnKeys={this.props.defaultHiddenColumnKeys}
        />
      </div>
    );
  }
}

function isStringValueFilter(filter: Filter): filter is StringFieldFilter {
  return typeof filter.value === 'string';
}

class ExpandedItemTableConnector extends DataConnector<Props, EnhancedProps> {
  override stores = [AssayStore, InventoryFileStore, SampleSchemaStore, StorableStore];

  enhanceProps: () => EnhancedProps;

  constructor(props: Props) {
    super(props);
    this.enhanceProps = preserveShallowEquality(this.uncachedEnhanceProps);
  }

  uncachedEnhanceProps = (): EnhancedProps => {
    // For purposes of fetching extras we need all of the items, including group children of expanded groups.
    const allItems = this.props.items.concat(
      (this.props.groups ?? []).filter((group) => group.isExpanded).flatMap((group) => group.items)
    );

    const bioentityIds = allItems.flatMap((item) => (FileUtil.isInventoryFile(item) ? [item.id] : []));
    //  No need to force refetch on a new search because ItemListing already does it.
    const bioentityAliasInfosByBioentityId = this.getOrFetchMultipleOptionalDeprecated(
      ManagedModelType.BIOENTITY_ALIAS_INFOS_BY_BIOENTITY_ID,
      bioentityIds
    );
    let validationExtrasByBioentityId = {};
    // Fetch validation extras for registry listing.
    if (this.props.finderId === REGISTRIES_FINDER_ID) {
      validationExtrasByBioentityId = this.getOrFetchMultipleOptionalDeprecated(
        ManagedModelType.VALIDATION_EXTRAS_BY_BIOENTITY_ID,
        bioentityIds
      );
      if (!this.props.asyncFetcherIsFetchedState[AsyncFetcherLocation.EXPANDED_ITEM_TABLE_ENHANCE_PROPS]) {
        this.forceFetchMultiple(ManagedModelType.VALIDATION_EXTRAS_BY_BIOENTITY_ID, bioentityIds);
      }
    }
    const baseEntryIds: Array<string> = allItems.flatMap((item) =>
      APIUtil.isBaseEntry(item.id) ? [item.id] : []
    );
    const reviewChangeTimestampsByEntryId = this.getOrFetchMultipleOptionalDeprecated(
      ManagedModelType.ENTRY_REVIEW_CHANGE_TIMESTAMPS_BY_ENTRY_ID,
      baseEntryIds
    );
    if (!this.props.asyncFetcherIsFetchedState[AsyncFetcherLocation.EXPANDED_ITEM_TABLE_ENHANCE_PROPS]) {
      this.forceFetchMultiple(ManagedModelType.ENTRY_REVIEW_CHANGE_TIMESTAMPS_BY_ENTRY_ID, baseEntryIds);
    }

    // TODO: We should make these explicit dependencies, and load if they're not already loaded. We're
    // currently assuming that Sidepanel loads the schemas.
    const storableIds = allItems.flatMap((item) => (isStorableSummary(item) ? [item.id] : []));
    const storableSummaryExtras = this.storableStore().getStorableSummaryExtras();
    if (!StorableSummaryExtrasManager.areKeysLoadedOrLoading(storableIds)) {
      // No need to force refetch on a new search because ItemListing already does it.
      for (const id of storableIds) {
        StorableSummaryExtrasManager.requestFetch(id);
      }
    }

    const state = this.reduxState();
    const {sampleStatusCountsByRequestId, requestedTaskSchemaIdsByRequestId} = state.models.requests.inputs;
    const {requestTaskSchemasById} = state.models.requests.schemas;

    return {
      bioentityAliasInfosByBioentityId,
      reviewChangeTimestampsByEntryId,
      validationExtrasByBioentityId,
      storableSummaryExtras,
      sampleStatusCountsByRequestId,
      requestedTaskSchemaIdsByRequestId,
      requestTaskSchemasById,
      itemAttributes: this.props.searchResults.attributes,
    };
  };
}

export const ExpandedItemTableInnerComponent = ExpandedItemTableConnector.connect(ExpandedItemTable);

const CONTAINER_CONTENT_CONTAINER_FRAGMENT = gql`
  fragment ContainerContentContainer on ContainerContentNode {
    id
    container(includeArchived: false) {
      ... on ContainerNode {
        id
        quantityJson
        storableSchema {
          id
          name
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
`;

export const EXPANDED_ITEM_TABLE_QUERY = gql`
  query ExpandedItemTableQuery($bioentityIds: [StrictString!]!) @skipAudit @engComponent(name: SIGHTGLASS) {
    bioentities(ids: $bioentityIds) {
      ... on RegistryItemInterface {
        id
        tagSchema {
          id
          isContainable
          childEntitySchemas {
            id
            isContainable
          }
        }
        linkedContainerContents {
          ...ContainerContentContainer
        }
        allChildEntities {
          id
          linkedContainerContents {
            ...ContainerContentContainer
          }
        }
      }
      ... on MixtureNode {
        id
        __typename
        amount
        units
        ingredients {
          id
          amount
          units
          componentBioentity {
            id
            name
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${CONTAINER_CONTENT_CONTAINER_FRAGMENT}
`;

export default function ExpandedItemTableGraphQLWrapper(props: Props): JSX.Element | null {
  const bioentityIds = props.items.flatMap((item) => (APIUtil.isBiologicalEntity(item.id) ? [item.id] : []));
  const selectedContainableTagSchemaIds = getSelectedContainableTagSchemaIds(props.searchParams);

  const starredItemsResult = useStarredItemsQuery(props.items);
  const itemStudyAttributes = useItemsInStudiesQuery(props.items);
  const containersValidContentsResult = useContainerValidContentsQuery(props.items);
  const containersExperimentRolesResult = useContainerExperimentalRoleQuery(props.items);
  const storablesValidationResult = useStorableValidationInfoQuery(props.items);
  const tagSchemas = useTagSchemaSummariesWithFields(selectedContainableTagSchemaIds.size === 0);
  const requestSchemasResult = useAllRequestSchemasQuery();
  const runSchemasResult = useAllRunSchemasQuery();

  const expandedItemTableResult = useSkippableBenchlingQuery<
    ExpandedItemTableQuery,
    ExpandedItemTableQueryVariables
  >(EXPANDED_ITEM_TABLE_QUERY, {
    skip: !BenchlingConfig.INVENTORY_ENABLE_SUMMARY_ON_ENTITY_SEARCH,
    variables: {bioentityIds},
  });

  const hasLatestBeenFetched = props.asyncFetcherIsFetchedState[AsyncFetcherLocation.EXPANDED_ITEM_TABLE_GQL];
  const asyncFetcherUpdate = props.onUpdateAsyncFetcherIsFetchedState;

  useEffect(() => {
    if (!hasLatestBeenFetched) {
      asyncFetcherUpdate(AsyncFetcherLocation.EXPANDED_ITEM_TABLE_GQL, true);
    }
  }, [asyncFetcherUpdate, hasLatestBeenFetched]);

  if (!hasLatestBeenFetched) {
    // Need to refetch the async fetchers that kick off after the search endpoint returns
    starredItemsResult.refetch();
    itemStudyAttributes?.refetch();
    containersValidContentsResult?.refetch();
    containersExperimentRolesResult?.refetch();
    storablesValidationResult?.refetch();
    expandedItemTableResult.refetch();
  }

  if (
    starredItemsResult.type === QueryResultType.PENDING ||
    (selectedContainableTagSchemaIds.size > 0 && tagSchemas == null) ||
    requestSchemasResult.type === QueryResultType.PENDING ||
    runSchemasResult.type === QueryResultType.PENDING
  ) {
    return <CenteredHorizontalThrobber />;
  }

  if (
    expandedItemTableResult.type === QueryResultType.ERROR ||
    starredItemsResult.type === QueryResultType.ERROR
  ) {
    return null;
  }

  let requestSchemas: Array<RequestSchemaGQL> = [];
  if (requestSchemasResult.type === QueryResultType.SUCCESS) {
    requestSchemas = requestSchemasResult.data.requestSchemas;
  }

  let runSchemas: Array<RunSchemaGQL> = [];
  if (runSchemasResult.type === QueryResultType.SUCCESS) {
    runSchemas = runSchemasResult.data.runSchemas;
  }

  const selectedContainableTagSchemas = (tagSchemas || []).filter(({id}) =>
    selectedContainableTagSchemaIds.has(id)
  );

  if (expandedItemTableResult.type === QueryResultType.SKIPPED) {
    return (
      <ExpandedItemTableInnerComponent
        {...props}
        itemStudyAttributes={itemStudyAttributes}
        containerValidContentsInfoById={containersValidContentsResult?.validContentsInfoByContainerId}
        experimentalRolesByContainerId={containersExperimentRolesResult?.experimentalRolesByContainerId}
        storableValidationInfoById={storablesValidationResult?.storableValidationInfoById}
        requestSchemas={requestSchemas}
        runSchemas={runSchemas}
      />
    );
  }

  let inventoryInfoByContainableId: {[bioentityId: string]: ContainableInventoryInfo} | 'LOADING';
  let mixturesById;
  if (expandedItemTableResult.type === QueryResultType.PENDING) {
    inventoryInfoByContainableId = 'LOADING';
  } else {
    const {data} = expandedItemTableResult;
    inventoryInfoByContainableId = objectFromEntries(
      accessibleObjs([...data.bioentities]).map((containableContainerInfo) =>
        entry(containableContainerInfo.id, containableContainerInfo)
      )
    );

    const mixtures: Array<ExpandedItemTableMixtureGraphQL> = accessibleObjs(data.bioentities).flatMap(
      (bioentity) => (bioentity.__typename === 'MixtureNode' ? [bioentity] : [])
    );
    mixturesById = objectFromEntries(mixtures.map((mixture) => entry(mixture.id, mixture)));
  }

  return (
    <ExpandedItemTableInnerComponent
      {...props}
      inventoryInfoByContainableId={inventoryInfoByContainableId}
      mixturesById={mixturesById}
      starredItemsByObjectId={starredItemsResult.starredItemsByObjectId}
      itemStudyAttributes={itemStudyAttributes}
      containerValidContentsInfoById={containersValidContentsResult?.validContentsInfoByContainerId}
      experimentalRolesByContainerId={containersExperimentRolesResult?.experimentalRolesByContainerId}
      storableValidationInfoById={storablesValidationResult?.storableValidationInfoById}
      selectedContainableTagSchemas={selectedContainableTagSchemas}
      requestSchemas={requestSchemas}
      runSchemas={runSchemas}
    />
  );
}
